import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import './HowItWorks.css';

// Custom hook for responsive container measurements
function useContainerSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;

    const updateSize = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setSize({ width: rect.width, height: rect.height });
      }
    };

    // Initial measurement
    updateSize();

    // Create ResizeObserver for dynamic updates
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(ref.current);

    // Also listen for window resize
    window.addEventListener('resize', updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [ref]);

  return size;
}

// Typing indicator component (three animated dots)
function TypingIndicator() {
  return (
    <div className="typing-indicator">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="typing-dot"
          animate={{
            y: [0, -4, 0],
            opacity: [0.4, 1, 0.4],
          }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            delay: i * 0.15,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

// Chat bubble component with iMessage-style animation
function ChatBubble({ bubble, index, isInView }) {
  const [showContent, setShowContent] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      // First show typing indicator, then reveal content
      const typingDelay = index * 800;
      const contentDelay = typingDelay + 600;

      const typingTimer = setTimeout(() => {
        setShowTyping(true);
      }, typingDelay);

      const contentTimer = setTimeout(() => {
        setShowTyping(false);
        setShowContent(true);
        controls.start("visible");
      }, contentDelay);

      return () => {
        clearTimeout(typingTimer);
        clearTimeout(contentTimer);
      };
    }
  }, [isInView, index, controls]);

  const bubbleVariants = {
    hidden: {
      opacity: 0,
      scale: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15,
        mass: 0.8,
      }
    },
    float: {
      y: [0, -3, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
      }
    }
  };

  return (
    <motion.div
      className={`chat-bubble-base ${bubble.type}`}
      initial="hidden"
      animate={showContent ? ["visible", "float"] : "hidden"}
      variants={bubbleVariants}
    >
      {showTyping && !showContent && <TypingIndicator />}

      {showContent && (
        <>
          {bubble.avatar && (
            <div className="chat-content">
              <div className="avatar-wrapper">
                <div className="avatar-background"></div>
                <img
                  src={bubble.avatar}
                  alt={`${bubble.type === 'user-bubble' ? 'User' : 'System'} avatar`}
                  className="avatar-image"
                />
              </div>
              <p className="chat-text">{bubble.text}</p>
            </div>
          )}
          {!bubble.avatar && (
            <p className="chat-text">{bubble.text}</p>
          )}
        </>
      )}
    </motion.div>
  );
}

function Step({ stepNumber, title, description, image, chatBubbles }) {
  const containerRef = useRef(null);
  const stepRef = useRef(null);
  const containerSize = useContainerSize(containerRef);
  const isEven = stepNumber % 2 === 0;

  // Trigger animations when step comes into view
  const isInView = useInView(stepRef, {
    once: true,
    amount: 0.3,
    margin: "-50px"
  });

  // Calculate responsive font size based on container width
  const getResponsiveFontSize = useCallback(() => {
    const { width } = containerSize;
    if (width === 0) return 14;
    if (width < 300) return 11;
    if (width < 400) return 12;
    if (width < 500) return 14;
    return 16;
  }, [containerSize]);

  // Calculate bubble scale based on container size
  const getBubbleScale = useCallback(() => {
    const { width } = containerSize;
    if (width === 0) return 1;
    if (width < 250) return 0.75;
    if (width < 350) return 0.85;
    if (width < 450) return 0.95;
    return 1;
  }, [containerSize]);

  const fontSize = getResponsiveFontSize();
  const scale = getBubbleScale();

  // Step content animation variants
  const contentVariants = {
    hidden: { opacity: 0, x: isEven ? 50 : -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, x: isEven ? -50 : 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  return (
    <div className="step" ref={stepRef}>
      <motion.div
        className="step-content"
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={contentVariants}
      >
        <h3>{title}</h3>
        <p>{description}</p>
      </motion.div>
      <motion.div
        className="step-image-wrapper"
        ref={containerRef}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={imageVariants}
      >
        {/* Chat conversation container - positioned like an iMessage thread */}
        <div
          className={`chat-conversation ${isEven ? 'conversation-left' : 'conversation-right'}`}
          style={{
            '--bubble-font-size': `${fontSize}px`,
            '--bubble-scale': scale,
          }}
        >
          {chatBubbles.map((bubble, index) => (
            <ChatBubble
              key={index}
              bubble={bubble}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
        <img src={image} alt={`Step ${stepNumber}`} className="step-image" />
      </motion.div>
    </div>
  );
}

export default Step;
